if (!customElements.get('ss-subscription-card')) {
  customElements.define('ss-subscription-card', class extends HTMLElement {
    connectedCallback() {
      this.copy = JSON.parse(this.querySelector('.ss-card__translations').textContent);
      this.subscriptionOnly = this.hasAttribute('data-subscription-only');
      this.toggle = this.querySelector('[role="switch"]');
      this.select = this.querySelector('select');
      this.trigger = this.querySelector('.ss-card__trigger');
      this.list = this.querySelector('.ss-card__options');
      this.toggle.setAttribute('aria-label', this.copy.subscription);
      this.trigger.setAttribute('aria-label', this.copy.interval);
      this.list.setAttribute('aria-label', this.copy.interval);
      this.querySelector('.ss-card__benefits').setAttribute('aria-label', this.copy.benefits);
      this.setText('.ss-card__shipping', this.copy.shipping);
      this.setText('.ss-card__blades', this.copy.blades);
      this.onOpen = () => this.setOpen(this.list.hidden);
      this.onChoose = e => {
        const option = e.target.closest('[role="option"]');
        if (option && this.list.contains(option)) this.choosePlan(option.dataset.value);
      };
      this.onOutside = e => { if (!this.querySelector('.ss-card__picker').contains(e.target)) this.setOpen(false); };
      this.onFocusOut = e => { if (!this.querySelector('.ss-card__picker').contains(e.relatedTarget)) this.setOpen(false); };
      this.onKey = e => {
        const options = Array.from(this.list.children);
        if (!options.length) return;
        const open = !this.list.hidden;
        if (e.key === 'Escape') {
          if (open) { e.preventDefault(); e.stopPropagation(); this.setOpen(false); }
          return;
        }
        if (e.key === 'Tab') { this.setOpen(false); return; }
        if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) {
          e.preventDefault();
          if (!open) this.setOpen(true);
          let index = this.activeIndex;
          if (e.key === 'Home') index = 0;
          else if (e.key === 'End') index = options.length - 1;
          else if (open) index += e.key === 'ArrowDown' ? 1 : -1;
          this.focusPlan(Math.max(0, Math.min(options.length - 1, index)));
          return;
        }
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (open) this.choosePlan(options[this.activeIndex]?.dataset.value);
          else this.setOpen(true);
        }
      };
      this.onListPointer = e => e.preventDefault();
      this.trigger.addEventListener('click', this.onOpen);
      this.trigger.addEventListener('keydown', this.onKey);
      this.trigger.addEventListener('focusout', this.onFocusOut);
      this.list.addEventListener('click', this.onChoose);
      this.list.addEventListener('pointerdown', this.onListPointer);
      document.addEventListener('pointerdown', this.onOutside);

      this.onToggle = () => {
        const next = this.toggle.getAttribute('aria-checked') === 'true' ? 'onetime' : 'subscription';
        const radio = this.native?.shadowRoot?.querySelector('input[name="purchaseOption"][value="' + next + '"]');
        if (!radio) return this.fallback();
        radio.click();
        requestAnimationFrame(() => this.sync());
      };
      this.onPlan = () => {
        const root = this.native?.shadowRoot?.querySelector('rc-selling-plans')?.shadowRoot;
        const radio = root && Array.from(root.querySelectorAll('input[name="rc_plan"]')).find(el => el.value === this.select.value);
        const nativeSelect = root?.querySelector('select');
        if (radio) radio.click();
        else if (nativeSelect) {
          nativeSelect.value = this.select.value;
          nativeSelect.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
        } else return this.fallback();
        requestAnimationFrame(() => this.sync());
      };
      this.toggle.addEventListener('click', this.onToggle);
      this.select.addEventListener('change', this.onPlan);
      this.timer = setInterval(() => this.sync(), 350);
      this.sync();
    }
    disconnectedCallback() {
      this.trigger?.removeEventListener('click', this.onOpen);
      this.trigger?.removeEventListener('keydown', this.onKey);
      this.trigger?.removeEventListener('focusout', this.onFocusOut);
      this.list?.removeEventListener('click', this.onChoose);
      this.list?.removeEventListener('pointerdown', this.onListPointer);
      document.removeEventListener('pointerdown', this.onOutside);

      clearInterval(this.timer);
      this.toggle?.removeEventListener('click', this.onToggle);
      this.select?.removeEventListener('change', this.onPlan);
      this.nativeContainer?.classList.remove('ss-recharge-native-hidden');
      this.restoreProductPrice();
    }
    restoreProductPrice() {
      this.productPrices?.forEach(({node, original}) => { if (node.isConnected) node.textContent = original; });
      this.productPrices = null;
    }
    fallback() {
      this.setOpen(false);
      this.hidden = true;
      this.nativeContainer?.classList.remove('ss-recharge-native-hidden');
      this.restoreProductPrice();
    }
    setOpen(open) {
      if (!this.trigger) return;
      this.list.hidden = !open;
      this.trigger.setAttribute('aria-expanded', String(open));
      if (open) {
        const options = Array.from(this.list.children);
        this.focusPlan(Math.max(0, options.findIndex(el => el.dataset.value === this.select.value)));
      } else {
        this.trigger.removeAttribute('aria-activedescendant');
        Array.from(this.list.children).forEach(el => el.removeAttribute('data-active'));
      }
    }
    focusPlan(index) {
      this.activeIndex = index;
      Array.from(this.list.children).forEach((el, i) => el.toggleAttribute('data-active', i === index));
      const active = this.list.children[index];
      if (active) this.trigger.setAttribute('aria-activedescendant', active.id);
    }
    choosePlan(id) {
      if (!id) return;
      this.select.value = id;
      this.onPlan();
      this.setOpen(false);
      this.trigger.focus();
    }
    renderPlans(plans, selected) {
      const signature = JSON.stringify(plans.map(p => [p.id, p.label]));
      if (this.menuSignature !== signature) {
        this.setOpen(false);
        this.list.replaceChildren(...plans.map((plan, index) => {
          const option = document.createElement('div');
          option.className = 'ss-card__option';
          option.setAttribute('role', 'option');
          option.id = this.list.id + '-' + index;
          option.dataset.value = plan.id;
          option.textContent = plan.label;
          if (this.subscriptionOnly) {
            const hint = document.createElement('span');
            hint.className = 'ss-card__option-hint';
            hint.textContent = this.frequencyHint(plan.label);
            option.append(hint);
          }
          return option;
        }));
        this.menuSignature = signature;
      }
      Array.from(this.list.children).forEach(el => el.setAttribute('aria-selected', String(el.dataset.value === selected.id)));
      this.setText('.ss-card__selected', selected.label);
      if (this.subscriptionOnly) this.setText('.ss-card__selected-hint', this.frequencyHint(selected.label));
    }
    text(element) {
      if (!element) return '';
      const copy = element.cloneNode(true);
      copy.querySelectorAll('.visually-hidden').forEach(el => el.remove());
      return copy.textContent.replace(/\s+/g, ' ').trim();
    }
    amount(value) {
      return Number(value.replace(/[^\d,.-]/g, '').replace(',', '.'));
    }
    frequency(value) {
      const months = /(?:kuuk|month|mois|monat)/i.test(value) && value.match(/\d+/)?.[0];
      return this.copy.intervals[months] || value;
    }
    frequencyHint(value) {
      const months = Object.keys(this.copy.intervals).find(key => this.copy.intervals[key] === value);
      return this.copy.hints[months] || this.copy.choose;
    }
    setText(selector, text) {
      const node = this.querySelector(selector);
      if (node.textContent !== text) node.textContent = text;
    }
    sync() {
      try {
        if (!this.native?.isConnected) {
          const section = this.closest('[id^="shopify-section-"]') || document;
          this.native = section.querySelector('recharge-subscription-widget[product-id="' + this.dataset.productId + '"]');
        }
        const root = this.native?.shadowRoot;
        const sub = root?.querySelector('input[name="purchaseOption"][value="subscription"]');
        const one = root?.querySelector('input[name="purchaseOption"][value="onetime"]');
        if (!sub || (!one && !this.subscriptionOnly)) return this.fallback();
        if (this.subscriptionOnly && !sub.checked) { sub.click(); return; }
        const subscribed = sub.checked;
        if (!subscribed) this.setOpen(false);
        const price = this.text(root.querySelector('[part~="rc-purchase-option__discounted-price"]'));
        const normal = this.text(root.querySelector('[part~="rc-purchase-option__price"]')) || (this.subscriptionOnly ? this.text(root.querySelector('[part~="rc-purchase-option__original-price"]')) || price : '');
        if (!price || !normal) return this.fallback();
        const plansRoot = root.querySelector('rc-selling-plans')?.shadowRoot;
        if (subscribed) {
          const radios = Array.from(plansRoot?.querySelectorAll('input[name="rc_plan"]') || []);
          const dropdown = plansRoot?.querySelector('select');
          const plans = radios.length ? radios.map(el => ({
            id: el.value,
            label: this.frequency(this.text(el.closest('label')?.querySelector('[part~="rc-plans-button__interval"]'))),
            selected: el.checked
          })) : Array.from(dropdown?.options || []).filter(el => el.value).map(el => ({id: el.value, label: this.frequency(el.textContent.trim()), selected: el.selected}));
          if (!plans.length || plans.some(plan => !plan.label)) return this.fallback();
          const signature = JSON.stringify(plans.map(plan => [plan.id, plan.label]));
          if (this.planSignature !== signature) {
            this.select.replaceChildren(...plans.map(plan => new Option(plan.label, plan.id)));
            this.planSignature = signature;
          }
          const selected = plans.find(plan => plan.selected);
          if (!selected) return this.fallback();
          this.select.value = selected.id;
          this.setText('.ss-card__frequency label', this.frequencyHint(selected.label));
          this.renderPlans(plans, selected);
        }
        const savings = Math.max(0, Math.floor((1 - this.amount(price) / this.amount(normal)) * 100 + 0.00001));
        const saveText = Number.isFinite(savings) && savings > 0 ? this.copy.save.replace('{saving}', savings) : this.copy.benefits;
        this.toggle.setAttribute('aria-checked', String(subscribed));
        this.toggleAttribute('data-onetime', !subscribed);
        this.setText('.ss-card__title', subscribed ? (savings > 0 ? this.copy.subscribeSave.replace('{saving}', savings) : this.copy.subscription) : this.copy.oneTime);
        this.setText('.ss-card__subtitle', subscribed ? this.copy.subtitle : this.copy.oneSubtitle);
        this.setText('.ss-card__price', subscribed ? price : normal);
        this.setText('.ss-card__compare', normal);
        this.querySelector('.ss-card__compare').hidden = !subscribed || savings === 0;
        this.querySelector('.ss-card__frequency').hidden = !subscribed;
        this.querySelector('.ss-card__benefits').hidden = false;
        this.querySelectorAll('.ss-card__benefits li > span:first-child').forEach(icon => {
          const symbol = subscribed ? '✓' : '×';
          if (icon.textContent !== symbol) icon.textContent = symbol;
        });
        this.setText('.ss-card__saving', saveText);
        this.setText('.ss-card__summary', subscribed ? this.copy.summary : this.copy.oneSummary);
        if (this.subscriptionOnly) {
          if (!this.productPrices) {
            const section = this.closest('[id^="shopify-section-"]');
            this.productPrices = Array.from(section?.querySelectorAll('.wt-product__price[role="status"] .wt-product__price__final') || []).map(node => ({node, original: node.textContent}));
          }
          this.productPrices.forEach(({node}) => { if (node.textContent !== price) node.textContent = price; });
        }
        this.nativeContainer = this.native.closest('.shopify-app-block') || this.native;
        this.hidden = false;
        this.nativeContainer.classList.add('ss-recharge-native-hidden');
      } catch (_) {
        this.fallback();
      }
    }
  });
}
